/*
  <media-gallery>: the product page gallery.

  Owns the three Splide sliders rendered by snippets/product-media-gallery.liquid:
  the main stage, the thumbnail rail and the fullscreen lightbox. Splide itself
  is loaded once by layout/theme.liquid.

  product-info.js calls setActiveMedia("<section id>-<media id>") when a
  variant with its own image is picked; the element answers by moving the
  stage to that slide. The element name and that one method are the whole
  contract with Dawn, so the rest of the file is free to be ours.

  Mounting happens in connectedCallback so a section re-rendered by the theme
  editor gets a working gallery without any extra wiring.
*/
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.main = null;
        this.thumbs = null;
        this.lightbox = null;
        this.lightboxEl = null;
        this.counterEl = null;
        this.mounted = false;
        this.onKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        if (this.mounted) return;
        if (typeof Splide === 'undefined') {
          // splide.min.js is deferred ahead of this file, so this only fires
          // if the library failed to load. Try once more when parsing ends.
          window.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
          return;
        }

        this.mainEl = this.querySelector('.tt-gallery__main');
        this.thumbsEl = this.querySelector('.tt-gallery__thumbs');
        this.lightboxEl = this.querySelector('.tt-lightbox');
        if (!this.mainEl) return;

        this.mounted = true;
        this.mountStage();
        this.mountCounter();
        this.mountLightbox();
        this.addEventListener('click', this.onPosterClick);
      }

      disconnectedCallback() {
        if (!this.mounted) return;
        this.mounted = false;
        this.removeEventListener('click', this.onPosterClick);
        document.removeEventListener('keydown', this.onKeydown);
        document.documentElement.classList.remove('tt-lightbox-lock');
        if (this.lightbox) this.lightbox.destroy(true);
        if (this.thumbs) this.thumbs.destroy(true);
        if (this.main) this.main.destroy(true);
        if (this.lightboxEl && this.lightboxEl.parentElement === document.body) this.lightboxEl.remove();
        if (this.counterEl) this.counterEl.remove();
        this.main = this.thumbs = this.lightbox = null;
      }

      /* ---------------------------------------------------------------
         Main stage and thumbnail rail
      --------------------------------------------------------------- */
      get stageSlides() {
        return Array.from(this.mainEl.querySelectorAll('.splide__list > .splide__slide:not(.splide__slide--clone)'));
      }

      mountStage() {
        /* Full bleed on a phone shows one edge-to-edge image at a time with a
           counter instead of the thumbnail strip, so it wants a real sliding
           carousel. Everywhere else keeps the cross-fade. Splide fixes the
           type at mount, so this is decided once, here. */
        const fullBleed = !!this.closest('.product--full-bleed');
        const onPhone = window.matchMedia('(max-width: 767px)').matches;
        const swipeCarousel = fullBleed && onPhone;
        const count = this.stageSlides.length;

        this.main = new Splide(this.mainEl, {
          type: swipeCarousel ? 'slide' : 'fade',
          rewind: true,
          arrows: false,
          pagination: false,
          drag: count > 1,
          /* the hero image is in the HTML; every other slide waits for
             Splide to fetch it as the visitor gets near */
          lazyLoad: 'nearby',
          preloadPages: 1,
          /* a drag that starts on a player is the player's business */
          noDrag: 'video, iframe, model-viewer, input, textarea, .splide__arrow',
        });

        if (this.thumbsEl) {
          this.thumbs = new Splide(this.thumbsEl, {
            direction: 'ttb',
            height: '550px',
            perPage: 4,
            gap: 10,
            isNavigation: true,
            pagination: false,
            arrows: true,
            breakpoints: {
              767: {
                direction: 'ltr',
                height: '83px',
                gap: 7,
                perPage: 3,
              },
            },
          });
          this.main.sync(this.thumbs);
        }

        /* an image slide opens the lightbox; a video slide is handled by its
           own poster button. Splide's click event already ignores drags. */
        this.main.on('click', (Slide) => {
          if (Slide.slide.dataset.mediaType === 'image') this.openLightbox(Slide.slide.dataset.mediaId);
        });

        /* leaving a slide pauses whatever was playing on it */
        this.main.on('move', () => this.pauseMedia());

        /* Splide wants the parent mounted before the synced child */
        this.main.mount();
        if (this.thumbs) this.thumbs.mount();
      }

      /* Called by product-info.js with "<section id>-<media id>". */
      setActiveMedia(mediaId) {
        if (!this.main) return;
        const index = this.stageSlides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index >= 0) this.main.go(index);
      }

      /* ---------------------------------------------------------------
         Video: poster first, player on tap
      --------------------------------------------------------------- */
      onPosterClick(event) {
        const poster = event.target.closest('.tt-gallery__poster');
        if (!poster) return;
        const holder = poster.closest('[data-deferred-media]');
        /* 3D models carry Dawn's product-model element, which loads itself */
        if (!holder) return;
        this.loadDeferred(holder);
      }

      loadDeferred(holder) {
        if (holder.dataset.loaded) return;
        const template = holder.querySelector('template');
        if (!template || !template.content.firstElementChild) return;

        this.pauseMedia();
        const player = template.content.firstElementChild.cloneNode(true);
        holder.dataset.loaded = 'true';
        holder.appendChild(player);

        if (player.tagName === 'VIDEO') {
          const played = player.play();
          if (played && played.catch) played.catch(() => {});
        }
        player.focus({ preventScroll: true });
      }

      /* Pauses only the players inside this gallery. Dawn's pauseAllMedia
         would also stop the autoplaying hero video further down the page. */
      pauseMedia() {
        this.querySelectorAll('video').forEach((video) => video.pause());
        this.querySelectorAll('.js-youtube').forEach((frame) => {
          frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });
        this.querySelectorAll('.js-vimeo').forEach((frame) => {
          frame.contentWindow.postMessage('{"method":"pause"}', '*');
        });
        this.querySelectorAll('product-model').forEach((model) => {
          if (model.modelViewerUI) model.modelViewerUI.pause();
        });
      }

      /* ---------------------------------------------------------------
         Counter pill for the full-bleed gallery on phones. Built for every
         width because the CSS decides when it shows.
      --------------------------------------------------------------- */
      mountCounter() {
        if (!this.closest('.product--full-bleed')) return;
        const total = this.stageSlides.length;
        if (total < 2) return;

        const counter = document.createElement('div');
        counter.className = 'tt-gallery-counter';
        counter.innerHTML =
          '<button type="button" class="tt-gallery-counter__arrow" data-dir="&lt;" aria-label="Previous image">&#8592;</button>' +
          '<span class="tt-gallery-counter__value" aria-live="polite">1 / ' + total + '</span>' +
          '<button type="button" class="tt-gallery-counter__arrow" data-dir="&gt;" aria-label="Next image">&#8594;</button>';

        const gallery = this.mainEl.closest('.product-gallery') || this;
        gallery.appendChild(counter);
        this.counterEl = counter;

        const value = counter.querySelector('.tt-gallery-counter__value');
        counter.addEventListener('click', (event) => {
          const arrow = event.target.closest('[data-dir]');
          if (arrow) this.main.go(arrow.dataset.dir);
        });
        this.main.on('move mounted', () => {
          value.textContent = this.main.index + 1 + ' / ' + total;
        });
      }

      /* ---------------------------------------------------------------
         Fullscreen zoom lightbox, images only
      --------------------------------------------------------------- */
      mountLightbox() {
        const el = this.lightboxEl;
        if (!el || !el.querySelector('.splide__slide')) {
          this.lightboxEl = null;
          return;
        }

        /* Re-parent to <body> so no ancestor stacking context (sticky gallery,
           AOS transforms) can trap the overlay underneath the site header. */
        document.body.appendChild(el);

        this.lightboxCounter = el.querySelector('.tt-lightbox__counter');
        this.lightboxTotal = el.querySelectorAll('.splide__slide').length;

        el.querySelector('.tt-lightbox__close').addEventListener('click', () => this.closeLightbox());

        /* Backdrop tap closes, but only if the pointer didn't travel (a swipe
           between slides also ends outside the image). */
        let downX = 0;
        let downY = 0;
        el.addEventListener('pointerdown', (event) => {
          downX = event.clientX;
          downY = event.clientY;
        });
        el.addEventListener('click', (event) => {
          if (event.target.closest('img, .splide__arrow, .tt-lightbox__close, .tt-lightbox__counter')) return;
          if (Math.abs(event.clientX - downX) > 10 || Math.abs(event.clientY - downY) > 10) return;
          this.closeLightbox();
        });

        document.addEventListener('keydown', this.onKeydown);
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.lightboxEl && this.lightboxEl.classList.contains('is-open')) {
          this.closeLightbox();
        }
      }

      openLightbox(mediaId) {
        const el = this.lightboxEl;
        if (!el) return;

        const slides = Array.from(el.querySelectorAll('.splide__list > .splide__slide:not(.splide__slide--clone)'));
        let index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index < 0) index = 0;

        /* Splide can't measure a hidden element, so the overlay must be
           visible before mounting. */
        this.lightboxOpener = document.activeElement;
        el.classList.add('is-open');
        document.documentElement.classList.add('tt-lightbox-lock');

        /* A fresh mount per open, starting on the clicked image. Mounting and
           then calling go() in the same frame never animates, so Splide never
           hears "moved" and the lazy loader never fetches the neighbours. */
        if (this.lightbox) this.lightbox.destroy(true);
        this.lightbox = new Splide(el.querySelector('.splide'), {
          type: 'slide',
          rewind: true,
          start: index,
          pagination: false,
          arrows: this.lightboxTotal > 1,
          drag: this.lightboxTotal > 1,
          speed: 250,
          lazyLoad: 'nearby',
          preloadPages: 1,
        });
        this.lightbox.on('move mounted', () => {
          if (this.lightboxCounter) {
            this.lightboxCounter.textContent = this.lightbox.index + 1 + '/' + this.lightboxTotal;
          }
        });
        this.lightbox.mount();
        el.focus({ preventScroll: true });
      }

      closeLightbox() {
        const el = this.lightboxEl;
        if (!el) return;
        el.classList.remove('is-open');
        document.documentElement.classList.remove('tt-lightbox-lock');
        if (this.lightbox) {
          this.lightbox.destroy(true);
          this.lightbox = null;
        }
        if (this.lightboxOpener && this.lightboxOpener.focus) {
          this.lightboxOpener.focus({ preventScroll: true });
        }
      }
    }
  );
}
